<script setup lang="ts">
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import type { FastCheckoutMethod } from '@design-system/composables/useFastCheckoutMethods'
import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'
import type { PdpEcommerceBlockProductFastCheckoutSideSlideProps } from './PdpEcommerceBlockProductFastCheckoutSideSlide.props'

/**
 * "Fast checkout" sidesheet, opened from the PDP when several fast payment
 * methods are available.
 *
 * It mirrors the minicart without the cart link and checkout CTA: the product
 * (editable quantity and variant), the order recap with the promo code and,
 * in place of the CTA, the wallet buttons rendered by their own SDKs — the
 * same components the minicart uses, pointed at the isolated PDP express order
 * via `source="express"`.
 */
const props = defineProps<PdpEcommerceBlockProductFastCheckoutSideSlideProps>()

const { ts } = useI18n()
const { currentWebsite, isEa7Website, isEmporioArmaniWebsite } = useWebsite()

/** EA7 and Emporio sidesheets are black; this fast checkout sheet stays white. */
const isLightSurface = computed(
  () => isEa7Website.value || isEmporioArmaniWebsite.value
)
const { availableMethods } = useFastCheckoutMethods()
const { isWalletReady } = useFastCheckoutIntent()
const { refresh } = useExpressOrder()
const {
  productCard,
  subtotal,
  discount,
  promoItems,
  selectedShippingMethod,
  estimatedTaxAmountFloat,
  estimatedTax,
  total,
} = useExpressOrderRecap()

const { isDialogOpen: isQuickBuyOpen } = useDialog(
  SIDEBAR_IDS.uiQuickBuyPopupSlideFastCheckout
)
const { isDialogOpen: isShippingSidebarOpen } = useDialog(
  SIDEBAR_IDS.cartShippingSidebar
)
const { isDialogOpen: isTaxesSidebarOpen } = useDialog(
  SIDEBAR_IDS.cartTaxesSideBar
)
const isNestedSidebarOpen = computed(
  () =>
    isQuickBuyOpen.value ||
    isShippingSidebarOpen.value ||
    isTaxesSidebarOpen.value
)

const isAvailable = (method: FastCheckoutMethod) =>
  !!availableMethods.value?.includes(method)

/** Shown from the PDP data until the express order holds the line item. */
const pdpCard = computed<ProductCardHorizontalProps>(() => ({
  id: props.productId,
  info: {
    name: props.product.title,
    price: props.product.price,
    oldPrice: props.product.oldPrice,
    color: props.product.selectedColor,
    size: props.selectedSizeLabel ?? '',
  },
  path: '#',
  quantity: 1,
  itemTotalPrice: props.product.price,
  image: props.image ?? '',
  metadata: currentWebsite.value,
  available: true,
  skuCode: props.productId,
}))

const card = computed<ProductCardHorizontalProps>(() =>
  productCard.value
    ? {
        ...productCard.value,
        image: productCard.value.image || pdpCard.value.image,
      }
    : pdpCard.value
)

const handleEstimatedTaxUpdate = async (value: number | null) => {
  estimatedTaxAmountFloat.value = value
  // The preview moved the express order to the entered zip code: re-read it
  // so the wallets charge the updated taxes.
  if (value !== null) await refresh()
}
</script>

<template>
  <OrganismsSidebarSlide
    :id="SIDEBAR_IDS.pdpEcommerceBlockFastCheckout"
    :force-light-theme="isLightSurface"
    :header-props="{ titleText: ts('pdp.fastCheckout.title') }"
    :force-open="isNestedSidebarOpen"
    :disable-click-to-close-outside="isQuickBuyOpen"
    body-class="overflow-x-hidden"
  >
    <template #body>
      <div class="text-neutral-black flex w-full flex-col gap-8 pb-10">
        <PdpEcommerceBlockProductFastCheckoutProductCard
          :card="card"
          :is-editable="!!productCard"
        />

        <div>
          <CartPriceSummary
            source="express"
            :subtotal="subtotal"
            :tax="estimatedTax"
            :total="total"
            :discount="discount"
            :shipping-method="selectedShippingMethod"
            :promo-items="promoItems"
            :enable-promo="true"
            @update:estimated-tax-amount-float="handleEstimatedTaxUpdate"
          >
            <template #cta>
              <PdpEcommerceBlockProductFastCheckoutWallet
                v-if="isAvailable('payPal')"
                method="payPal"
                surface="layer"
                :is-sdk-ready="isWalletReady"
                :loading="!isWalletReady"
                container-id="pdp-fast-checkout-paypal-container"
              />
              <PdpEcommerceBlockProductFastCheckoutWallet
                v-if="isAvailable('applePay')"
                method="applePay"
                surface="layer"
                :is-sdk-ready="isWalletReady"
                :loading="!isWalletReady"
                container-id="pdp-fast-checkout-applepay-container"
              />
              <PdpEcommerceBlockProductFastCheckoutWallet
                v-if="isAvailable('googlePay')"
                method="googlePay"
                surface="layer"
                :is-sdk-ready="isWalletReady"
                :loading="!isWalletReady"
                container-id="pdp-fast-checkout-googlepay-container"
              />
            </template>
          </CartPriceSummary>
        </div>
      </div>

      <!-- SECOND LEVEL SIDEBARS -->
      <CartShippingSideBar is-nested />
      <CartTaxesSideBar is-nested />
    </template>
  </OrganismsSidebarSlide>
</template>
